// src/store/notifications/action.ts
import { toast } from 'react-toastify';
import { NotificationActionTypes, Notification } from './types';
import { AppThunk } from '../auth/action';
import config from 'config';

const { DTS_API_NOTIFICATION } = config.api;

const controllerMap: Record<string, AbortController> = {};

interface FetchParams {
	page?: number;
	size?: number;
	query?: string;
	type?: string;
	channel?: string;
	append?: boolean;
	scope?: 'center' | 'dropdown';
}

// ─── fetch notifications ────────────────────────────────────────────────────
export const getNotifications: AppThunk = ({
	page = 1,
	size = 10,
	query = '',
	type,
	channel,
	append = false,
	scope = 'center',
}: FetchParams = {}) => {
	return async (dispatch, getState) => {
		const token = getState().auth.token;
		const key = scope;

		// abort any in-flight request for this scope
		if (controllerMap[key]) controllerMap[key].abort();
		controllerMap[key] = new AbortController();

		dispatch({
			type: NotificationActionTypes.GET_NOTIFICATIONS_REQUEST,
			meta: { scope },
		});

		const params = new URLSearchParams({ page: `${page}`, size: `${size}`, query });
		if (type) params.append('type', type);
		if (channel) params.append('channel', channel);

		try {
			const res = await fetch(
				`${DTS_API_NOTIFICATION}/api/notifications?${params.toString()}`,
				{
					headers: { Authorization: `Bearer ${token}` },
					signal: controllerMap[key].signal,
				}
			);

			// HTTP-level error?
			if (!res.ok) {
				let errText = '';
				try { errText = await res.text(); } catch { }
				const msg = `Error ${res.status} fetching notifications${errText ? `: ${errText}` : ''}`;
				dispatch({
					type: NotificationActionTypes.GET_NOTIFICATIONS_ERROR,
					payload: msg,
					meta: { scope },
				});
				toast.error(msg);
				return;
			}

			// parse body
			const body = await res.json();

			dispatch({
				type: NotificationActionTypes.GET_NOTIFICATIONS_SUCCESS,
				payload: {
					content: body.content || [],
					pagination: body.pagination || {},
				},
				meta: { scope, append },
			});
		} catch (err: any) {
			if (err.name === 'AbortError') {
				// request was cancelled
				return;
			}
			const msg = err.message || 'Network error fetching notifications';
			dispatch({
				type: NotificationActionTypes.GET_NOTIFICATIONS_ERROR,
				payload: msg,
				meta: { scope },
			});
			toast.error(msg);
		}
	};
};

// ─── mark single as read ────────────────────────────────────────────────────
export const markAsRead: AppThunk = (id: string, scope: 'center' | 'dropdown') => {
	return async (dispatch, getState) => {
		dispatch({
			type: NotificationActionTypes.MARK_AS_READ_REQUEST,
			payload: id,
			meta: { scope },
		});

		const token = getState().auth.token;
		try {
			const res = await fetch(
				`${DTS_API_NOTIFICATION}/api/notifications/${id}/read`,
				{
					method: 'PATCH',
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (!res.ok) {
				const body = await res.text().catch(() => '');
				const msg = `Error ${res.status} marking notification as read${body ? `: ${body}` : ''}`;
				dispatch({
					type: NotificationActionTypes.MARK_AS_READ_ERROR,
					payload: id,
					meta: { scope },
				});
				toast.error(msg);
			} else {
				dispatch({
					type: NotificationActionTypes.MARK_AS_READ_SUCCESS,
					payload: id,
					meta: { scope },
				});
			}
		} catch (err: any) {
			const msg = err.message || 'Network error marking as read';
			dispatch({
				type: NotificationActionTypes.MARK_AS_READ_ERROR,
				payload: id,
				meta: { scope },
			});
			toast.error(msg);
		}
	};
};

// ─── mark all as read ───────────────────────────────────────────────────────
export const markAllAsRead: AppThunk = (scope: 'center' | 'dropdown') => {
	return async (dispatch, getState) => {
		dispatch({
			type: NotificationActionTypes.MARK_ALL_AS_READ_REQUEST,
			meta: { scope },
		});

		const token = getState().auth.token;
		try {
			const res = await fetch(
				`${DTS_API_NOTIFICATION}/api/notifications/read-all`,
				{
					method: 'PATCH',
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (!res.ok) {
				const body = await res.text().catch(() => '');
				const msg = `Error ${res.status} marking all as read${body ? `: ${body}` : ''}`;
				dispatch({
					type: NotificationActionTypes.MARK_ALL_AS_READ_ERROR,
					meta: { scope },
				});
				toast.error(msg);
			} else {
				dispatch({
					type: NotificationActionTypes.MARK_ALL_AS_READ_SUCCESS,
					meta: { scope },
				});
			}
		} catch (err: any) {
			const msg = err.message || 'Network error marking all as read';
			dispatch({
				type: NotificationActionTypes.MARK_ALL_AS_READ_ERROR,
				meta: { scope },
			});
			toast.error(msg);
		}
	};
};

// ─── SSE new notification ──────────────────────────────────────────────────
export const sseNewNotification = (notification: Notification) => ({
	type: NotificationActionTypes.SSE_NEW_NOTIFICATION,
	payload: notification,
});
